// Copyright (c) Ping. All rights reserved.
//
// Offline-first hooks for the business directory.
//
// Data flows:
//   1. Read from local Dexie immediately (renders without any network).
//   2. Background-sync from the server; Dexie write triggers liveQuery
//      re-render in all subscribed components.
//
// `useMyCompanies`       — user's own companies (ConversationListPanel sidebar)
// `useDirectoryCompanies`— public browseable listing (BusinessDirectoryView)

import { useCallback, useEffect, useRef, useState } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';

import { useSessionStore } from '@ping/ui/src/messaging/sessionStore';

import { db, type DirectoryCompanyRecord,type MyCompanyRecord } from '@core/persistence/db';

import type { Category, Company, CompanyListFilters } from './directoryApi';
import { getCompanies,getMyCompanies } from './directoryApi';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Convert a server Company to a DirectoryCompanyRecord for local storage. */
function toDirectoryRecord(c: Company, now: number): DirectoryCompanyRecord {
  return {
    id: c.id,
    ownerAccountId: c.ownerAccountId,
    name: c.name,
    description: c.description,
    website: c.website,
    logoUrl: c.logoUrl,
    locationCity: c.locationCity,
    locationCountry: c.locationCountry,
    employeeRange: c.employeeRange,
    sector: c.sector,
    foundedYear: c.foundedYear,
    categorySlugs: c.categories.map((cat) => cat.slug),
    categoryNames: c.categories.map((cat) => cat.name),
    verified: c.verified,
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
    syncedAt: now,
  };
}

/** Convert a DirectoryCompanyRecord (or MyCompanyRecord) back to a Company. */
export function recordToCompany(r: DirectoryCompanyRecord): Company {
  return {
    id: r.id,
    ownerAccountId: r.ownerAccountId,
    name: r.name,
    description: r.description,
    website: r.website,
    logoUrl: r.logoUrl,
    locationCity: r.locationCity,
    locationCountry: r.locationCountry,
    employeeRange: r.employeeRange as Company['employeeRange'],
    sector: r.sector,
    foundedYear: r.foundedYear,
    categories: r.categorySlugs.map((slug, i) => ({
      slug,
      name: r.categoryNames[i] ?? slug,
    })),
    verified: r.verified,
    verification: null,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

/** Does a cached directory row satisfy the (server-equivalent) filters? */
function matchesFilters(r: DirectoryCompanyRecord, filters: CompanyListFilters): boolean {
  if (filters.search) {
    const q = filters.search.toLowerCase();
    if (!r.name.toLowerCase().includes(q) && !r.description.toLowerCase().includes(q)) {
      return false;
    }
  }
  if (
    filters.locationCity &&
    r.locationCity?.toLowerCase() !== filters.locationCity.toLowerCase()
  ) {
    return false;
  }
  if (
    filters.locationCountry &&
    r.locationCountry?.toLowerCase() !== filters.locationCountry.toLowerCase()
  ) {
    return false;
  }
  if (filters.employeeRange && r.employeeRange !== filters.employeeRange) return false;
  if (filters.categories?.length) {
    if (!filters.categories.some((s) => r.categorySlugs.includes(s))) return false;
  }
  return true;
}

// ─── Public-directory offline cache (used by BusinessDirectoryView) ────────────
//
// The public listing is server-paginated + server-filtered, but we mirror each
// fetched page into `db.directory_companies` so a repeat/offline visit renders
// instantly and a failed fetch can fall back to the cache instead of a bare
// error. Categories are cached in `settings_kv` for the same reason.

const CATEGORIES_CACHE_KEY = 'directory.categories.v1';

/** Warm the public-directory Dexie cache with a page of server results. */
export async function cacheDirectoryCompanies(companies: readonly Company[]): Promise<void> {
  if (companies.length === 0) return;
  const now = Date.now();
  try {
    await db.directory_companies.bulkPut(companies.map((c) => toDirectoryRecord(c, now)));
  } catch {
    // Best-effort cache write — never block the UI on a cache miss.
  }
}

/** Read the cached public directory, applying the same filters as the server. */
export async function readCachedDirectoryCompanies(
  filters: CompanyListFilters = {},
): Promise<readonly Company[]> {
  try {
    const rows = await db.directory_companies.toArray();
    return rows.filter((r) => matchesFilters(r, filters)).map(recordToCompany);
  } catch {
    return [];
  }
}

/** Persist the category list so the filter sidebar / create form work offline. */
export async function cacheCategories(categories: readonly Category[]): Promise<void> {
  if (categories.length === 0) return;
  try {
    await db.settings_kv.put({
      key: CATEGORIES_CACHE_KEY,
      value: categories,
      updatedAt: Date.now(),
    });
  } catch {
    // Best-effort.
  }
}

/** Read the cached category list ([] when absent). */
export async function readCachedCategories(): Promise<readonly Category[]> {
  try {
    const row = await db.settings_kv.get(CATEGORIES_CACHE_KEY);
    return Array.isArray(row?.value) ? (row.value as Category[]) : [];
  } catch {
    return [];
  }
}

// ─── useMyCompanies ───────────────────────────────────────────────────────────

export interface UseMyCompaniesResult {
  companies: readonly Company[];
  loading: boolean;
  syncing: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Reads the current user's companies from local Dexie (instant) and
 * background-syncs with the server. The liveQuery subscription means
 * any Dexie write — including optimistic ones from CreateCompanyPage —
 * will re-render components automatically.
 */
export function useMyCompanies(): UseMyCompaniesResult {
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const syncLock = useRef(false);

  // `/v1/directory/companies/mine` is owner-scoped (RequireJWT). This hook
  // lives in the always-mounted chat sidebar (ConversationListPanel), which
  // renders during `silentBootstrap` BEFORE the access token is set in memory
  // — firing the sync then sends a tokenless request → 401 `invalid_token`.
  // Gate the sync on the authenticated session so it only fires once a token
  // exists (and re-fires when auth becomes ready).
  const authed = useSessionStore((s) => s.status.kind === 'authenticated');

  const rows = useLiveQuery(() => db.my_companies.toArray(), [], []);
  const loading = rows === undefined;

  const sync = useCallback(async () => {
    if (syncLock.current) return;
    syncLock.current = true;
    setSyncing(true);
    setError(null);
    try {
      const serverCompanies = await getMyCompanies();
      const now = Date.now();
      // Bulk-put to Dexie — liveQuery triggers re-render.
      await db.my_companies.bulkPut(
        serverCompanies.map((c) => ({
          ...toDirectoryRecord(c, now),
          syncState: 'synced' as const,
        })),
      );
      // Remove rows that were deleted server-side (not in new snapshot).
      const serverIds = new Set(serverCompanies.map((c) => c.id));
      const localRows = await db.my_companies.toArray();
      const staleIds = localRows
        .filter((r) => r.syncState === 'synced' && !serverIds.has(r.id))
        .map((r) => r.id);
      if (staleIds.length > 0) {
        await db.my_companies.bulkDelete(staleIds);
      }
    } catch (err) {
      setError((err as Error).message ?? 'Failed to sync companies.');
    } finally {
      setSyncing(false);
      syncLock.current = false;
    }
  }, []);

  // Sync once authenticated (token present); caller can also call refresh().
  // Re-runs when `authed` flips true after a cold-shell silent re-auth.
  useEffect(() => {
    if (!authed) return;
    void sync();
  }, [sync, authed]);

  const companies: readonly Company[] = (rows ?? []).map(recordToCompany);

  return { companies, loading, syncing, error, refresh: sync };
}

// ─── useDirectoryCompanies ────────────────────────────────────────────────────

export interface UseDirectoryCompaniesResult {
  companies: readonly Company[];
  total: number;
  loading: boolean;
  syncing: boolean;
  error: string | null;
  refresh: () => void;
}

const DIRECTORY_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Reads cached directory companies from Dexie, then background-syncs
 * from the server. The sync respects a 5-minute TTL so the network
 * isn't hit on every page visit. Pass `filters` to narrow the listing;
 * the local read applies the same text/location filters in-JS.
 */
export function useDirectoryCompanies(
  filters: CompanyListFilters = {},
): UseDirectoryCompaniesResult {
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const syncLock = useRef(false);

  // `/v1/directory/companies` is also behind RequireJWT — gate the background
  // sync on the authenticated session so it never fires tokenless during the
  // pre-bootstrap window (same 401 cause as `useMyCompanies`).
  const authed = useSessionStore((s) => s.status.kind === 'authenticated');

  const rows = useLiveQuery(() => db.directory_companies.toArray(), [], []);
  const loading = rows === undefined;

  const sync = useCallback(
    async (force = false) => {
      if (syncLock.current) return;
      // Respect TTL unless forced.
      if (!force) {
        const newest = await db.directory_companies.orderBy('syncedAt').last();
        if (newest && Date.now() - newest.syncedAt < DIRECTORY_CACHE_TTL_MS) return;
      }
      syncLock.current = true;
      setSyncing(true);
      setError(null);
      try {
        const result = await getCompanies({ ...filters, limit: 100 });
        const now = Date.now();
        await db.directory_companies.bulkPut(
          result.companies.map((c) => toDirectoryRecord(c, now)),
        );
        setTotal(result.total);
      } catch (err) {
        setError((err as Error).message ?? 'Failed to sync directory.');
      } finally {
        setSyncing(false);
        syncLock.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.search, filters.locationCity, filters.locationCountry, filters.categories?.join(',')],
  );

  useEffect(() => {
    if (!authed) return;
    void sync();
  }, [sync, authed]);

  // Apply the same (server-equivalent) filters to the cached rows.
  const filtered = (rows ?? []).filter((r) => matchesFilters(r, filters));

  const companies: readonly Company[] = filtered.map(recordToCompany);

  return {
    companies,
    total: total || companies.length,
    loading,
    syncing,
    error,
    refresh: () => { void sync(true); },
  };
}

// ─── Optimistic write helpers ─────────────────────────────────────────────────

/**
 * Optimistically write a newly-created company to both local tables
 * before the server confirms. Call this immediately after the user
 * submits step 1 so the sidebar shows the company instantly.
 * The background sync in useMyCompanies will later flip syncState to 'synced'.
 */
export async function persistCompanyOptimistically(company: Company): Promise<void> {
  const now = Date.now();
  const record: MyCompanyRecord = {
    ...toDirectoryRecord(company, now),
    // 'pending' — NOT 'synced'. The stale-delete in `useMyCompanies` removes
    // any 'synced' row missing from the server LIST snapshot; marking the
    // optimistic row 'pending' protects it until a `/mine` sync actually
    // returns it (which then flips it to 'synced'). Marking it 'synced' here
    // would let a sync that races ahead of read-after-write delete it.
    syncState: 'pending',
  };
  try {
    await db.my_companies.put(record);
    await db.directory_companies.put(toDirectoryRecord(company, now));
  } catch {
    // Best-effort — if Dexie is unavailable the UI still works via network.
  }
}
